import { Checkbox, Dialog, Menu, MenuItem } from "@mui/material";
import {
  ButtonComponent,
  CommonTable,
  ConfirmationPopup,
  PageLimitDropDownComponent,
  PaginationComponent,
  SearchComponent,
  TableStatusDropdown,
} from "@shopeasy-dashboard-ui/ui-library";
import {
  ArrowLeft,
  Edit,
  ExternalLink,
  Globe,
  MoreVertical,
  PlusCircle,
  Trash,
  Trash2,
} from "feather-icons-react/build/IconComponents";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Bag, BoxSeam, SlashCircle } from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import API from "../../API/newIndex";
import { DataLoad } from "../../App";
import Breadcrumbs from "../../controllers/breadCrumbs";
import { GetClient, clientData as globalClientData } from "../../controllers/GetClient";
import { parentNavigate } from "../../controllers/ParentNavigate";
import { TableColumnsContext } from "../../controllers/TableColumnsContext";
import { useUser } from "../../controllers/UserContext";
import NoRecords from "../Main/NoRecords";
import Notification from "../Notification";
import BulkEditVendor from "./BulkEditVendor";
import useTableApi from "../../utilities/useTableApi";

const getVendorPreviewUrl = (row, clientInfo) => {
  const slug = row?.shop_slug || row?.slug;
  if (!slug) return null;
  const protocol = typeof window !== "undefined" ? window.location.protocol + "//" : "https://";
  const domain = clientInfo?.set_primary_domain
    ? `www.${clientInfo?.custom_domain}`
    : clientInfo?.customer_domain;

  if (!domain) return null;

  let routePrefix = "/vendor";
  if (clientInfo?.route_manage) {
    const { routes } = clientInfo.route_manage;
    if (routes?.length) {
      const typeToName = Object.fromEntries(routes.map((r) => [r.type, r.name]));
      const routeName = typeToName["vendor"] || typeToName["vendors"];
      if (routeName) {
        routePrefix = `/${routeName}`;
      }
    }
  }

  const prefixPart = routePrefix ? (routePrefix.startsWith("/") ? routePrefix : `/${routePrefix}`) : "";
  return `${protocol}${domain}${prefixPart}/${slug}`;
};

export const defaultVendorsField = [
  "image",
  "name",
  "email",
  "phone",
  "registered",
  "status",
  "action",
];

export const VENDOR_SORT_KEYS = [
  "name",
  "email",
  "phone",
  "registered",
  "status",
  "createdAt",
  "vendor_join_date",
  "vendor_tagline",
  "shop_name",
  "shop_slug",
];

const isAscendingOrder = (order) => {
  if (!order) return false;
  if (typeof order === "string") {
    const upper = order.trim().toUpperCase();
    return upper === "ASC" || upper === "A-Z" || upper === "1" || upper === "TRUE";
  }
  if (typeof order === "object") {
    if (order.orderByValue !== undefined) {
      const upper = String(order.orderByValue).trim().toUpperCase();
      return upper === "ASC" || upper === "A-Z" || upper === "1" || upper === "TRUE";
    }
    if (order.order_by !== undefined) {
      const upper = String(order.order_by).trim().toUpperCase();
      return upper === "ASC" || upper === "A-Z" || upper === "1" || upper === "TRUE";
    }
    if (order.isDecending !== undefined) {
      return order.isDecending === false || order.isDecending === "false";
    }
    if (order.isDescending !== undefined) {
      return order.isDescending === false || order.isDescending === "false";
    }
    if (order.isAscending !== undefined) {
      return order.isAscending === true || order.isAscending === "true";
    }
  }
  return false;
};

const stripHtml = (html) => {
  if (typeof html !== "string") return html;
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
};

const VendorsList = () => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const { dataLoading, setDataLoading } = useContext(DataLoad) || { dataLoading: false, setDataLoading: () => { } };
  const { tableColumnsData } = useContext(TableColumnsContext);
  const { sortData, isUserLoaded } = useUser();
  const userContext = useUser();

  const savedSort = sortData?.vendors;
  const apiFieldNames = useMemo(() => {
    const dataObj = tableColumnsData?.data?.data || tableColumnsData?.data || tableColumnsData;
    if (Array.isArray(dataObj?.vendors) && dataObj.vendors.length > 0) return dataObj.vendors;
    if (Array.isArray(dataObj?.vendor) && dataObj.vendor.length > 0) return dataObj.vendor;
    if (Array.isArray(dataObj?.allVendors) && dataObj.allVendors.length > 0) return dataObj.allVendors;
    if (Array.isArray(tableColumnsData?.vendors) && tableColumnsData.vendors.length > 0) return tableColumnsData.vendors;
    if (Array.isArray(tableColumnsData?.vendor) && tableColumnsData.vendor.length > 0) return tableColumnsData.vendor;
    if (Array.isArray(tableColumnsData?.allVendors) && tableColumnsData.allVendors.length > 0) return tableColumnsData.allVendors;
    return defaultVendorsField;
  }, [tableColumnsData]);

  const dynamicColumns = useMemo(() => {
    const fixedKeys = new Set([
      '_id', '__v', 'client', 'tenant', 'store_client', 'store_tenant', 'user',
      'checkbox', 'selection', 'select', 'drag_handle', 'draghandle', 'drag',
      'image', 'name', 'email', 'phone', 'registered', 'status', 'action', 'actions', "createdAt", "profile_picture"
    ]);

    return apiFieldNames
      .filter((fieldName) => typeof fieldName === 'string' && fieldName.trim() && !fixedKeys.has(fieldName))
      .map((fieldName) => {
        const isDotted = fieldName.includes('.');
        const parts = isDotted ? fieldName.split('.') : null;

        const label = fieldName
          .replace(/\./g, ' › ')
          .replace(/[-_]/g, ' ')
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .replace(/\b\w/g, (c) => c.toUpperCase());

        const getValue = (row) => {
          if (!row) return undefined;
          if (isDotted && parts) {
            return parts.reduce((obj, k) => (obj != null ? obj[k] : undefined), row);
          }
          return row[fieldName];
        };

        return {
          key: fieldName,
          title: label,
          width: '160px',
          minWidth: '110px',
          responsiveWidth: '100px',
          headerClassName: 'px-3 whitespace-nowrap truncate',
          cellClassName: 'px-3 truncate',
          render: (row) => {
            const val = getValue(row);
            if (val === null || val === undefined || val === '') return '-';
            if (typeof val === 'boolean') return val ? 'Yes' : 'No';
            if (
              typeof val === 'string' &&
              (fieldName.toLowerCase().includes('date') ||
                fieldName.toLowerCase().includes('createdat') ||
                fieldName.toLowerCase().includes('updatedat') ||
                fieldName.toLowerCase().endsWith('at') ||
                /^\d{4}-\d{2}-\d{2}T/.test(val))
            ) {
              return formatDateString(val);
            }
            if (typeof val === 'object') return JSON.stringify(val);
            const strVal = typeof val === 'string' ? stripHtml(val) : String(val);
            return <span className="line-clamp-2 truncate">{strVal || '-'}</span>;
          },
        };
      });
  }, [apiFieldNames]);

  const handleNavigate = (path) => {
    parentNavigate(path);
    if (
      path.startsWith("/vendors") ||
      path.startsWith("/vendors/update") ||
      path.startsWith("/vendors/add")
    ) {
      navigate(path);
    }
  };

  const handleTableApi = useTableApi();
  const [search, setSearch] = useState("");
  const [openTab, setOpenTab] = useState("all"); // 'all', 'enabled', 'pending', 'disabled'
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [limit, setLimit] = useState(localStorage.getItem("limit") || 10);
  const [counts, setCounts] = useState({ all: 0, enabled: 0, pending: 0, disabled: 0 });

  const [selectedRowsId, setSelectedRowsId] = useState([]);

  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState(null);

  // Sorting state & handler
  const [sortBy, setSortBy] = useState("createdAt");
  const [orderBy, setOrderBy] = useState({
    orderByValue: "DESC",
    isDecending: true,
  });

  const isInitialSortApplied = useRef(false);

  const SortData = (sort_by, explicitOrder) => {
    setSortBy(sort_by);
    let nextOrder = "DESC";
    if (explicitOrder !== undefined) {
      nextOrder = isAscendingOrder(explicitOrder) ? "ASC" : "DESC";
    } else if (sortBy === sort_by) {
      const isCurrentlyAsc = isAscendingOrder(orderBy);
      nextOrder = isCurrentlyAsc ? "DESC" : "ASC";
    } else {
      nextOrder = "DESC";
    }

    const newOrderPayload = {
      orderByValue: nextOrder,
      isDecending: nextOrder === "DESC",
    };
    setOrderBy(newOrderPayload);
    setPage(1);
  };

  // 3-dots Menu state
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [activeVendorRow, setActiveVendorRow] = useState(null);
  const [openBulkEditModal, setOpenBulkEditModal] = useState(false);

  const [notification, setNotification] = useState({
    show: false,
    message: "",
    severity: "info",
  });

  const handleBulkEdit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (selectedRowsId.length > 50) {
      setNotification({
        show: true,
        message: "Maximum 50 vendors can be bulk edited at a time.",
        severity: "error",
      });
      return;
    }
    setOpenBulkEditModal(true);
  };

  const fetchVendors = async () => {
    setLoading(true);
    if (setDataLoading) setDataLoading(true);
    try {
      await GetClient();
      const res = await API({
        url: "/admin/vendor/list",
        method: "get",
        params: {
          page,
          limit,
          search: search?.trim() || undefined,
          status: openTab !== "all" ? openTab : undefined,
          sort_by: sortBy,
          order_by: orderBy.orderByValue,
        },
      });
      if (res?.data?.data) {
        const records = res.data.data.records || res.data.data.vendors || res.data.data || [];
        setVendors(records);
        const total = res.data.data.total || records.length;
        setTotalRecords(total);
        setTotalPages(res.data.data.totalPages || Math.ceil(total / limit) || 1);

        if (res.data.data.counts) {
          const c = res.data.data.counts;
          setCounts({
            all: (c.all || 0).toString().padStart(2, "0"),
            enabled: (c.enabled || 0).toString().padStart(2, "0"),
            pending: (c.pending || 0).toString().padStart(2, "0"),
            disabled: (c.disabled || 0).toString().padStart(2, "0"),
          });
        } else {
          setCounts((prev) => ({
            ...prev,
            all: total.toString().padStart(2, "0"),
          }));
        }
      } else {
        setVendors([]);
      }
    } catch (err) {
      console.error("Error fetching vendors:", err);
      setNotification({
        show: true,
        message: err?.response?.data?.message || err.message || "Error fetching vendors",
        severity: "error",
      });
      setVendors([]);
    } finally {
      setLoading(false);
      if (setDataLoading) setDataLoading(false);
    }
  };

  useEffect(() => {
    // If user has token, wait until user preferences are loaded from server
    if (!isUserLoaded && localStorage.getItem("token")) return;

    // Apply saved sort from user preferences before first fetch if present
    if (!isInitialSortApplied.current) {
      isInitialSortApplied.current = true;
      if (savedSort?.sort_by) {
        const isAsc =
          savedSort.order_by === "ASC" ||
          savedSort.order_by === "asc" ||
          savedSort.isDecending === false ||
          savedSort.orderByValue === "ASC";

        const isCurrentAsc = orderBy.orderByValue === "ASC" || orderBy.isDecending === false;
        if (savedSort.sort_by !== sortBy || isAsc !== isCurrentAsc) {
          setSortBy(savedSort.sort_by);
          setOrderBy({
            orderByValue: isAsc ? "ASC" : "DESC",
            isDecending: !isAsc,
          });
          return; // State update triggers the fetch with correct saved sort
        }
      }
    }

    fetchVendors();
  }, [page, limit, openTab, search, sortBy, orderBy, isUserLoaded]);

  const handleSelectRow = (e, id) => {
    if (id === "all") {
      if (e.target.checked) {
        setSelectedRowsId(vendors.map((v) => v._id));
      } else {
        setSelectedRowsId([]);
      }
    } else {
      if (e.target.checked) {
        setSelectedRowsId((prev) => [...prev, id]);
      } else {
        setSelectedRowsId((prev) => prev.filter((item) => item !== id));
      }
    }
  };

  const handleToggleStatus = async (vendor) => {
    handleCloseActionMenu();
    const currentStatus = (
      vendor?.vendor_selling_status ||
      vendor?.status ||
      "disabled"
    ).toLowerCase();
    const isEnabled = currentStatus === "enabled";
    const newStatus = isEnabled ? "disabled" : "enabled";
    try {
      await API({
        url: `/admin/vendor/status/${vendor._id}`,
        method: "put",
        data: { vendor_selling_status: newStatus },
      });
      setNotification({
        show: true,
        message: `Vendor status updated to ${newStatus === "enabled" ? "Enabled" : "Disabled"}`,
        severity: "success",
      });
      fetchVendors();
    } catch (err) {
      console.error("Status update error:", err);
      setNotification({
        show: true,
        message: err?.response?.data?.message || err.message || "Failed to update status",
        severity: "error",
      });
    }
  };

  const handleDeleteVendor = async () => {
    try {
      const idsToDelete = vendorToDelete ? [vendorToDelete._id] : selectedRowsId;
      for (const id of idsToDelete) {
        await API({
          url: `/admin/vendor/delete/${id}`,
          method: "delete",
        });
      }
      setNotification({
        show: true,
        message: idsToDelete.length > 1 ? "Selected vendors deleted" : "Vendor deleted successfully",
        severity: "success",
      });
      setSelectedRowsId([]);
      fetchVendors();
    } catch (err) {
      console.error("Delete vendor error:", err);
      setNotification({
        show: true,
        message: err?.response?.data?.message || err.message || "Failed to delete vendor",
        severity: "error",
      });
    } finally {
      setDeleteConfirmation(false);
      setVendorToDelete(null);
    }
  };

  const handleOpenActionMenu = (event, vendor) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setActiveVendorRow(vendor);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setActiveVendorRow(null);
  };

  const formatDateString = (dateVal) => {
    if (!dateVal) return "-";
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const columns = useMemo(
    () => [
      {
        key: "checkbox",
        title: (
          <div className="flex items-center justify-center w-full">
            <Checkbox
              size="small"
              className="!p-0"
              checked={vendors.length > 0 && selectedRowsId.length === vendors.length}
              indeterminate={selectedRowsId.length > 0 && selectedRowsId.length < vendors.length}
              onChange={(e) => handleSelectRow(e, "all")}
            />
          </div>
        ),
        align: "center",
        ignoreRowClick: true,
        headerClassName: "!px-0 text-center !overflow-visible !text-clip [text-overflow:clip]",
        cellClassName: "!px-0 text-center !overflow-visible !text-clip [text-overflow:clip]",
        render: (row) => (
          <div className="flex items-center justify-center w-full">
            <Checkbox
              size="small"
              className="!p-0"
              checked={selectedRowsId.includes(row._id)}
              onChange={(e) => handleSelectRow(e, row._id)}
            />
          </div>
        ),
      },
      {
        key: "image",
        title: "Image",
        headerClassName: "px-3 py-3 text-left w-[70px] min-w-[70px]",
        cellClassName: "px-3 py-3 text-left w-[70px] min-w-[70px]",
        render: (row) => (
          <div className="w-10 h-10 rounded-lg bg-[var(--primary-bg-color)] text-[var(--primary-text-color)] flex items-center justify-center font-bold text-xs shrink-0 overflow-hidden border main-border-color">
            {row.profile_picture || row.avatar ? (
              <img
                src={row.profile_picture || row.avatar}
                alt={row.shop_name || row.name}
                className="w-full h-full object-cover"
              />
            ) : row.vendor_logo || row.logo ? (
              <img
                src={row.vendor_logo || row.logo}
                alt={row.shop_name || row.name}
                className="w-full h-full object-cover"
              />
            ) : (
              (row.shop_name ? row.shop_name[0] : row.name ? row.name[0] : "V").toUpperCase()
            )}
          </div>
        ),
      },
      {
        key: "name",
        title: "Name",
        headerClassName: "px-4 py-3 text-left w-[200px] min-w-[200px]",
        cellClassName: "px-4 py-3 text-left w-[200px] min-w-[200px]",
        render: (row) => {
          const previewUrl = getVendorPreviewUrl(row, globalClientData);
          return (
            <div>
              <div className="font-semibold text-xs primary-text-color">
                {row.shop_name || row.name}
              </div>
              {previewUrl ? (
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 mt-1 rounded-md text-[11px] font-medium border main-border-color transition-all duration-150 group shrink-0"
                  title={`Preview Store: ${previewUrl}`}
                >
                  <Globe className="w-3 h-3 opacity-70 text-primary group-hover:opacity-100 transition-opacity shrink-0" />
                  <span className="text-primary">Visit Store</span>
                  <ExternalLink className="w-2.5 h-2.5 opacity-60 group-hover:opacity-100 transition-transform shrink-0" />
                </a>
              ) : null}
            </div>
          );
        },
      },
      {
        key: "email",
        title: "Email",
        headerClassName: "px-4 py-3 text-left w-full min-w-[200px]",
        cellClassName: "px-4 py-3 text-left text-xs table-text w-full min-w-[200px]",
        render: (row) => (
          <span className="text-xs table-text">
            {row.email || "—"}
          </span>
        ),
      },
      {
        key: "phone",
        title: "Phone",
        headerClassName: "px-4 py-3 text-left whitespace-nowrap w-[160px] min-w-[160px]",
        cellClassName: "px-4 py-3 text-left text-xs table-text whitespace-nowrap w-[160px] min-w-[160px]",
        render: (row) => <span className="table-text text-xs">{row.phone || "—"}</span>,
      },
      {
        key: "registered",
        title: "Registered",
        headerClassName: "px-4 py-3 text-right whitespace-nowrap w-[140px] min-w-[140px]",
        cellClassName: "px-4 py-3 text-right text-xs table-text whitespace-nowrap w-[140px] min-w-[140px]",
        render: (row) => <span className="table-text text-xs">{formatDateString(row.createdAt)}</span>,
      },
      {
        key: "status",
        title: "Status",
        headerClassName: "px-4 py-3 text-center whitespace-nowrap w-[110px] min-w-[110px]",
        cellClassName: "px-4 py-3 text-center whitespace-nowrap w-[110px] min-w-[110px]",
        align: "text-center",
        ignoreRowClick: true,
        render: (row) => {
          const rawStatus = (row.vendor_selling_status || row.status || "disabled").toLowerCase();
          const isApproved = rawStatus === "enabled";
          const isPending = rawStatus === "pending";

          return (
            <div className="flex items-center justify-center w-full">
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold capitalize ${isApproved
                  ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800"
                  : isPending
                    ? "bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-200 dark:border-amber-800"
                    : "bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400 border border-rose-200 dark:border-rose-800"
                  }`}
              >
                {rawStatus}
              </span>
            </div>
          );
        },
      },
      ...dynamicColumns,
      {
        key: "action",
        title: "Action",
        headerClassName: "px-4 py-3 text-left whitespace-nowrap w-[110px] min-w-[110px]",
        cellClassName: "px-4 py-3 text-left whitespace-nowrap w-[110px] min-w-[110px]",
        align: "text-left",
        ignoreRowClick: true,
        render: (row) => (
          <div className="flex items-center justify-center w-full gap-2">
            <button
              onClick={() => handleNavigate(`/vendors/${row._id}`)}
              title="Edit Vendor"
            >
              <Edit className='w-[17px] h-[17px]' />
            </button>
            <button
              onClick={() => {
                setVendorToDelete(row);
                setDeleteConfirmation(true);
              }}
              className='text-xs font-thin text-red-500 hover:text-red-600'
              title="Delete Vendor"
            >
              <Trash className='w-[17px] h-[17px]' />
            </button>
            <button
              onClick={(e) => handleOpenActionMenu(e, row)}
              className="text-primary flex items-center justify-center"
              title="More Options"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
          </div>
        ),
      },
    ],
    [vendors, selectedRowsId, dynamicColumns]
  );

  const tableHeaderFilter = (manageFieldsBtn) => (
    <div className="relative w-full border-b main-border-color secondary-bg-color px-3 py-2 flex flex-col pe-3 min-h-[50px]">
      <div className="flex flex-wrap items-center justify-between w-full gap-2 min-h-[38px]">
        {/* Status Dropdown */}
        <div className="flex items-center">
          <TableStatusDropdown
            value={openTab}
            onChange={(val) => {
              setOpenTab(val);
              setPage(1);
            }}
            options={[
              { label: "All", value: "all", count: (counts.all || 0).toString().padStart(2, "0") },
              { label: "Approved", value: "enabled", count: (counts.enabled || 0).toString().padStart(2, "0") },
              { label: "Pending", value: "pending", count: (counts.pending || 0).toString().padStart(2, "0") },
              { label: "Disabled", value: "disabled", count: (counts.disabled || 0).toString().padStart(2, "0") },
            ]}
            placeholder="All"
          />
        </div>

        {/* Search, Limit & Columns Dropdown on Right */}
        <div className="hidden md:flex items-center gap-2 ml-auto">
          <div className="w-[180px] md:w-[200px]">
            <SearchComponent
              placeholder="Search..."
              value={search}
              onChange={(e) => {
                setSearch(e?.target ? e.target.value : (e || ""));
                setPage(1);
              }}
              onClear={() => {
                setSearch("");
                setPage(1);
              }}
              isClearable={true}
              size="sm"
            />
          </div>

          <div className="w-[80px] md:w-[100px] shrink-0 select-sm-height select-hide-label select-no-padding-y">
            <PageLimitDropDownComponent
              limit={limit}
              setLimit={setLimit}
              setPage={setPage}
              totalRecords={totalRecords}
            />
          </div>
          {manageFieldsBtn}
        </div>
      </div>

      {/* Bulk Action Bar on Selection */}
      {totalRecords > 0 && selectedRowsId.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 mt-1 w-full">
          <span className="text-sm px-2 whitespace-nowrap">
            {selectedRowsId.length} Selected
          </span>
          <span className="me-1">|</span>
          <ButtonComponent
            variant="outline"
            size="sm"
            className="mx-1"
            onClick={handleBulkEdit}
            icon={<Edit className="w-3.5 h-3.5" />}
          >
            Bulk Edit
          </ButtonComponent>
          <ButtonComponent
            variant="danger_outline"
            size="sm"
            icon={<Trash2 className="w-3.5 h-3.5" />}
            onClick={(e) => {
              e.preventDefault();
              setVendorToDelete(null);
              setDeleteConfirmation(true);
            }}
          >
            Delete selection
          </ButtonComponent>
        </div>
      )}
    </div>
  );

  var startRec = (page - 1) * limit + 1;
  var endRec = Math.min(page * limit, totalRecords);

  return (
    <>
      <ConfirmationPopup
        heading="Delete Vendor Account"
        subheading={
          vendorToDelete
            ? `Do you really want to delete ${vendorToDelete.shop_name || vendorToDelete.name}?`
            : `Do you really want to delete ${selectedRowsId.length} selected vendor(s)?`
        }
        confirmationPopup={deleteConfirmation}
        handleCloseConfirm={(val) => setDeleteConfirmation(val)}
        runFunction={handleDeleteVendor}
      />

      {notification.show && (
        <Notification
          message={notification.message}
          close={() => setNotification((prev) => ({ ...prev, show: false }))}
          severity={notification.severity}
        />
      )}

      <div className="h-auto overflow-y-auto overflow-x-hidden pb-[64px] p-5">
        <div className="flex items-end justify-between page-header-pb">
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                parentNavigate("/dashboard");
              }}
              className="back-arrow"
            >
              <ArrowLeft />
            </button>
            <div>
              <Breadcrumbs
                crumbs={[
                  { name: "Dashboard", path: "/dashboard" },
                  { name: "Vendor Management", path: "/vendors" },
                ]}
              />
              <h4 className="text-xl md:text-2xl font-semibold text-primary">
                Vendor Management
              </h4>
            </div>
          </div>
          <div className="flex space-x-3 items-center">
            <ButtonComponent
              variant="primary"
              size="md"
              icon={<PlusCircle className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />}
              onClick={() => {
                handleNavigate("/vendors/add");
              }}
            >
              Add New Vendor
            </ButtonComponent>
          </div>
        </div>

        {/* Table View */}
        <div className="flex w-full h-full overflow-hidden relative flex-col">
          <CommonTable
            tableId="vendors"
            userState={userContext}
            tableHeader={tableHeaderFilter}
            tableFooter={
              totalRecords > 0 ? (
                <PaginationComponent
                  count={totalPages}
                  page={page}
                  onChange={(e, v) => setPage(v)}
                  totalRecords={totalRecords}
                  startRecord={startRec}
                  endRecord={endRec}
                  limit={limit}
                  recordsLabel="Vendors"
                />
              ) : null
            }
            columns={columns}
            data={vendors}
            loading={loading || dataLoading}
            skeletonRows={10}
            onSort={SortData}
            sortBy={sortBy}
            orderBy={orderBy}
            NEWAPI={handleTableApi}
            tableDefaults={defaultVendorsField}
            tableWiseAllowedSortKeys={VENDOR_SORT_KEYS}
            emptyMessage={<NoRecords />}
            rowClick={(row) => handleNavigate(`/vendors/detail/${row._id}`)}
          />
        </div>
      </div>

      {/* 3-Dots Action Dropdown Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
        elevation={3}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            style: {
              borderRadius: "8px",
              minWidth: "160px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              padding: "4px 0",
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            const v = activeVendorRow;
            handleCloseActionMenu();
            if (v) {
              handleNavigate(`/vendors/${v._id}`);
            }
          }}
          className="!text-xs font-medium primary-text-color py-2 px-4 hover:bg-[var(--focus-tab-color)] flex items-center gap-2.5"
        >
          <Edit className="w-4 h-4 table-text" />
          <span>Edit</span>
        </MenuItem>

        <MenuItem
          onClick={() => {
            const v = activeVendorRow;
            handleCloseActionMenu();
            if (v) {
              handleNavigate(`/products?vendor=${v._id}`);
            }
          }}
          className="!text-xs font-medium primary-text-color py-2 px-4 hover:bg-[var(--focus-tab-color)] flex items-center gap-2.5"
        >
          <BoxSeam className="w-4 h-4 table-text" />
          <span>See Products</span>
        </MenuItem>

        <MenuItem
          onClick={() => {
            const v = activeVendorRow;
            handleCloseActionMenu();
            if (v) {
              handleNavigate(`/orders?vendor=${v._id}`);
            }
          }}
          className="!text-xs font-medium primary-text-color py-2 px-4 hover:bg-[var(--focus-tab-color)] flex items-center gap-2.5"
        >
          <Bag className="w-4 h-4 table-text" />
          <span>See Orders</span>
        </MenuItem>

        <MenuItem
          onClick={() => {
            const v = activeVendorRow;
            if (v) {
              handleToggleStatus(v);
            }
          }}
          className="!text-xs font-medium primary-text-color py-2 px-4 hover:bg-[var(--focus-tab-color)] flex items-center gap-2.5"
        >
          <SlashCircle className="w-4 h-4 table-text" />
          <span>
            {(() => {
              const statusStr = (
                activeVendorRow?.vendor_selling_status ||
                activeVendorRow?.status ||
                ""
              ).toLowerCase();
              const isEnabled = statusStr === "enabled";
              return isEnabled ? "Disable Selling" : "Enable Selling";
            })()}
          </span>
        </MenuItem>
      </Menu>

      {openBulkEditModal && (
        <Dialog
          open={openBulkEditModal}
          disableEscapeKeyDown
          onClose={() => setOpenBulkEditModal(false)}
          fullScreen
          sx={{
            zIndex: 99999,
          }}
        >
          <BulkEditVendor
            isBulkEdit={true}
            bulkVendorIds={selectedRowsId}
            onClose={() => {
              setOpenBulkEditModal(false);
              setSelectedRowsId([]);
            }}
            onSuccess={(updatedCount) => {
              setOpenBulkEditModal(false);
              setSelectedRowsId([]);
              fetchVendors();
              setNotification({
                show: true,
                message: `${updatedCount} vendor${updatedCount > 1 ? "s" : ""} updated successfully`,
                severity: "success",
              });
            }}
          />
        </Dialog>
      )}
    </>
  );
};

export default VendorsList;
